/**
 * cards-soft-delete — hook pra arquivar/reativar cards (BUG-V2-047).
 *
 * Endpoint: PATCH /api/v1/cards/:type/:id/ativo
 * Backend (server/routes/cards-soft-delete.ts) gateaa por
 * is_admin OR pode_inativar_cards. UI deve esconder o botão quando user
 * não tem permissão (defensive — backend de qualquer jeito retorna 403).
 *
 * Invalida queries de comercial e service-orders pós-sucesso pra Kanban
 * re-renderizar (card some quando arquivado, ou volta com badge cinza
 * quando "Mostrar inativos" estiver ON).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from './api';

export type CardSoftDeleteType = 'deal' | 'service_order';

interface InativarCardInput {
  type: CardSoftDeleteType;
  id: string;
  ativo: boolean;
}

interface InativarCardResponse {
  ok: true;
  id: string;
  type: CardSoftDeleteType;
  ativo: boolean;
}

/**
 * Hook canônico pra alterar `ativo` de um card. Retorna mutation TanStack
 * Query com state `isPending` pra disable do botão.
 *
 * Uso típico:
 *   const inativar = useInativarCard();
 *   inativar.mutate({ type: 'deal', id: deal.id, ativo: false });
 */
export function useInativarCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: InativarCardInput): Promise<InativarCardResponse> => {
      return api.patch<InativarCardResponse>(
        `/api/v1/cards/${input.type}/${encodeURIComponent(input.id)}/ativo`,
        { ativo: input.ativo },
      );
    },
    onSuccess: (_data, vars) => {
      // BUG-V2-051: invalidação ABRANGENTE — antes deals e service-orders
      // tinham queryKeys errados (`['service-orders']` sem `v1` prefix).
      // Mapa real (verificado via grep):
      //   - Deals lista:   ['v1', 'deals', 'list', workflow]
      //   - Deals single:  ['v1', 'deals', 'single', id]
      //   - OS:            ['v1', 'service-orders', ...]
      //   - Activity:      ['v1', 'cards', cardId, 'activity', ...]
      // Prefix invalidation cobre TODOS os filhos do queryKey passado.
      // Card activity timeline também ganha entry 'archived'/'unarchived' —
      // invalidar pra aba "Atividade" atualizar sem F5.
      if (vars.type === 'deal') {
        void qc.invalidateQueries({ queryKey: ['v1', 'deals'] });
      } else {
        void qc.invalidateQueries({ queryKey: ['v1', 'service-orders'] });
      }
      // Activity timeline do card (mesma queryKey pra deal e OS).
      void qc.invalidateQueries({ queryKey: ['v1', 'cards', vars.id] });
    },
  });
}
